/**
 * RefreshChip — the persistent, unobtrusive progress surface for background
 * refreshes (rail-mounted, visible from EVERY page incl. the guide). The
 * chip shows the live phase + counts ("Narrating 12/74"); clicking expands
 * a popover with the full progress detail (progress bar, tokens, the
 * map-usable milestone) and Cancel. On SUCCESS the chip turns "Map updated"
 * for a beat, then fades (RefreshTracker dismisses it); a failed or
 * cancelled job stays put until the reader closes it from the popover.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useCancelJob, useRepo } from '../../api/queries';
import { formatTokens } from '../../lib/format';
import { Button, Spinner } from '../../lib/ui';
import { chipSummary, isMapUsable, PHASE_LABELS } from './jobProgress';
import { useRefreshJobs, type TrackedRefreshJob } from './refreshJobsStore';

/**
 * One job's full progress detail — the popover body. Keeps the testids the
 * modal panel carried (`job-progress`, `map-usable`, `job-done`) so the
 * progress contract stays assertable end to end.
 */
function JobProgressDetail({ job }: { job: TrackedRefreshJob }) {
  const cancelJob = useCancelJob();
  const repo = useRepo(job.repoId);
  const { state } = job;
  const total = state.planStale;
  const progressed = state.passProgress;
  const mapUsable = isMapUsable(state);

  return (
    <div className="space-y-3" data-testid="job-progress">
      {repo.data !== undefined ? (
        <p className="m-0 truncate font-medium text-primary">{repo.data.name}</p>
      ) : null}

      {mapUsable ? (
        <p className="m-0 text-(--positive)" data-testid="map-usable">
          Map usable:{' '}
          {total !== null
            ? `${Math.max(0, total - progressed)} areas still narrating`
            : 'narrating the rest'}{' '}
          in the background.
        </p>
      ) : null}

      {state.done === null ? (
        <div className="flex items-center justify-between gap-3">
          <Spinner
            label={
              mapUsable
                ? 'Narrating the rest in the background'
                : state.phase !== null
                  ? PHASE_LABELS[state.phase]
                  : 'Starting…'
            }
          />
          <Button
            kind="ghost"
            testId="refresh-cancel"
            disabled={cancelJob.isPending}
            onClick={() => cancelJob.mutate(job.jobId)}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div
          className={state.done.status === 'succeeded' ? 'text-(--positive)' : 'text-(--danger)'}
          data-testid="job-done"
        >
          {state.done.status === 'succeeded'
            ? state.narratePasses === 0
              ? 'Done. The code is read; the map is not written yet.'
              : 'Done. The map is current.'
            : state.done.status === 'cancelled'
              ? 'Cancelled. Completed work is kept.'
              : state.done.status === 'interrupted'
                ? 'The app restarted mid-refresh. Completed work is kept, and the refresh resumes on its own.'
                : 'Failed. Completed work is kept; see the job for details.'}
        </div>
      )}

      {total !== null && total > 0 && state.done === null ? (
        <div>
          <div className="mb-1 flex justify-between text-tertiary">
            <span>
              {progressed} of {total}
              {state.lastNodeId !== null ? (
                <span className="mono"> · {state.lastNodeId}</span>
              ) : null}
            </span>
            <span>
              {formatTokens(state.tokensIn)} in / {formatTokens(state.tokensOut)} out
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-pill bg-inset">
            <div
              className="h-full bg-(--accent) transition-[width] duration-300"
              style={{ width: `${Math.min(100, Math.round((progressed / total) * 100))}%` }}
            />
          </div>
        </div>
      ) : null}

      {state.connectionLost ? (
        <p className="m-0 text-(--warning)">
          Connection lost. Reconnecting picks up where it left off.
        </p>
      ) : null}
    </div>
  );
}

export function RefreshChip() {
  // Select stable references and derive (a fresh array from the selector
  // would loop useSyncExternalStore).
  const jobsById = useRefreshJobs((s) => s.jobs);
  const order = useRefreshJobs((s) => s.order);
  const jobs = useMemo(
    () =>
      order
        .map((id) => jobsById[id])
        .filter((j): j is TrackedRefreshJob => j !== undefined),
    [order, jobsById],
  );
  const dismiss = useRefreshJobs((s) => s.dismiss);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Auto-dismissal (the success fade) waits while the popover is open.
  useEffect(() => {
    useRefreshJobs.getState().setInspecting(open);
    return () => useRefreshJobs.getState().setInspecting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const first = jobs[0];
  if (first === undefined) return null;

  const done = first.state.done;
  const succeeded = done !== null && done.status === 'succeeded';
  const failed = done !== null && done.status !== 'succeeded';

  return (
    <div ref={rootRef} className="relative w-full px-1.5">
      <button
        type="button"
        data-testid="refresh-chip"
        data-state={done === null ? 'running' : succeeded ? 'succeeded' : done.status}
        aria-expanded={open}
        title={done === null ? 'Refreshing in the background. Click for details' : chipSummary(first.state)}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full cursor-pointer items-center gap-2 rounded-pill border px-2 py-1 text-[length:var(--text-micro-size)] whitespace-nowrap transition-colors duration-(--motion-hover) ${
          succeeded
            ? 'border-(--positive) text-(--positive)'
            : failed
              ? 'border-(--danger) text-(--danger)'
              : 'border-(--accent) text-(--accent)'
        } bg-raised`}
      >
        {done === null ? (
          <span aria-hidden className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-pill bg-(--accent)" />
        ) : (
          <span aria-hidden className="shrink-0">
            {succeeded ? '✓' : '✕'}
          </span>
        )}
        <span data-testid="refresh-chip-label" className="min-w-0 truncate">
          {chipSummary(first.state)}
        </span>
      </button>

      {open ? (
        <div
          data-testid="refresh-popover"
          className="absolute bottom-full left-1.5 z-50 mb-2 w-80 rounded-card border border-default bg-raised p-4 shadow-overlay"
        >
          <div className="space-y-4">
            {jobs.map((job) => (
              <JobProgressDetail key={job.jobId} job={job} />
            ))}
            {done !== null ? (
              <div className="flex justify-end">
                <Button
                  kind="primary"
                  onClick={() => {
                    setOpen(false);
                    for (const job of jobs) {
                      if (job.state.done !== null) dismiss(job.jobId);
                    }
                  }}
                >
                  Close
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
